import type {
  ProfileStatsResponse,
  ActivityHeatmapResponse,
  SuggestionsResponse,
  PendingLevelUp,
} from "@atlas/shared";
import { apiGet, apiPost, apiPatch } from "../lib/api-client";
import type { StoreItemMetadata, StoreItemRarity } from "./store";

export type {
  ProfileStats,
  RecentUnlock,
  ProfileStatsResponse,
  HeatmapEntry,
  ActivityHeatmapResponse,
  Suggestion,
  SuggestionsResponse,
  PendingLevelUp,
} from "@atlas/shared";

// ─── Equipped cosmetics (resolved) ────────────────────────────────────────────
// GET /profile/stats resolves the user's equipped item ids (see store.ts's
// InventoryEquipped for the raw-id shape used by the equip drawer) into full
// item details. Not declared in @atlas/shared yet — extending here keeps the
// change scoped to packages/web.

export interface EquippedCosmeticItem {
  itemId: string;
  slug: string;
  name: string;
  metadata: StoreItemMetadata;
  rarity: StoreItemRarity;
}

export interface EquippedCosmetics {
  badges: EquippedCosmeticItem[];
  banner: EquippedCosmeticItem | null;
  title: EquippedCosmeticItem | null;
  mapTheme: EquippedCosmeticItem | null;
  frame: EquippedCosmeticItem | null;
  traceStyle: EquippedCosmeticItem | null;
  stamp: EquippedCosmeticItem | null;
  pin: EquippedCosmeticItem | null;
}

export interface ProfileStatsWithCosmeticsResponse extends ProfileStatsResponse {
  waypoints: number;
  equipped: EquippedCosmetics;
  /** Free base theme persisted per profile (ATL-28); null = client default.
   * An equipped store map_theme skin always wins over this. */
  themePreference: string | null;
  pendingLevelUp: PendingLevelUp | null;
}

export function fetchProfileStats(): Promise<ProfileStatsWithCosmeticsResponse> {
  return apiGet("/profile/stats");
}

/** Persists the free base theme per profile (ATL-28). */
export function updateThemePreference(theme: string): Promise<{ themePreference: string | null }> {
  return apiPatch("/profile/theme", { theme });
}

/** Clears pendingLevelUp so the level-up overlay never re-fires for this level. */
export function ackLevelUp(): Promise<{ ok: boolean }> {
  return apiPost("/profile/level-up/ack");
}

export function fetchActivityHeatmap(year?: number | null): Promise<ActivityHeatmapResponse> {
  return apiGet(
    year != null ? `/profile/activity-heatmap?year=${year}` : "/profile/activity-heatmap",
  );
}

export function fetchSuggestions(): Promise<SuggestionsResponse> {
  return apiGet("/profile/suggestions");
}
